import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Droplets, Clock } from 'lucide-react';

// ─── Simulation Config ────────────────────────────────────────────────────────

const CAPACITY = 5;
const REFILL_RATE = 1;

const RAW_EVENTS = [
  { type: 'init' },
  { type: 'request', label: 'GET /api/users' },
  { type: 'request', label: 'GET /api/posts' },
  { type: 'tick' },
  { type: 'request', label: 'POST /api/comment' },
  { type: 'request', label: 'GET /api/feed' },
  { type: 'request', label: 'GET /api/search' },
  { type: 'request', label: 'POST /api/like' },       // last token
  { type: 'request', label: 'DELETE /api/post/42' },  // REJECTED
  { type: 'request', label: 'GET /api/profile' },     // REJECTED
  { type: 'tick' },                                    // +1 token
  { type: 'request', label: 'GET /api/profile' },     // allowed
  { type: 'tick' },
  { type: 'tick' },
  { type: 'tick' },
  { type: 'tick' },                                    // back to full (5)
  { type: 'tick' },                                    // capped — no change
  { type: 'request', label: 'GET /api/burst-1' },
  { type: 'request', label: 'GET /api/burst-2' },
  { type: 'request', label: 'GET /api/burst-3' },
  { type: 'request', label: 'GET /api/burst-4' },
  { type: 'request', label: 'GET /api/burst-5' },     // last token again
  { type: 'request', label: 'GET /api/burst-6' },     // REJECTED
];

// ─── Step Engine ──────────────────────────────────────────────────────────────

function buildSteps() {
  const steps = [];
  let tokens = CAPACITY;
  let eventLog = [];
  let totalRequests = 0;
  let allowedCount = 0;
  let rejectedCount = 0;

  for (const event of RAW_EVENTS) {
    if (event.type === 'init') {
      steps.push({
        id: 0,
        phase: 'init',
        tokens,
        eventLog: [],
        totalRequests,
        allowedCount,
        rejectedCount,
        currentEntry: null,
        title: 'Initialize Token Bucket',
        badge: null,
        explanation:
          `Token bucket initialized with ${CAPACITY} tokens (full capacity). ` +
          `Every incoming API request must consume 1 token from the bucket. ` +
          `If the bucket is empty when a request arrives, that request is rejected with HTTP 429 Too Many Requests. ` +
          `Tokens refill at a rate of ${REFILL_RATE} per tick, capped at ${CAPACITY}. ` +
          `This allows short bursts of up to ${CAPACITY} back-to-back requests while still ` +
          `enforcing a sustained long-term rate of ${REFILL_RATE} request per tick.`,
      });
    } else if (event.type === 'request') {
      totalRequests++;
      const tokensBefore = tokens;
      const allowed = tokens > 0;
      if (allowed) { tokens--; allowedCount++; } else { rejectedCount++; }
      const entry = {
        id: eventLog.length,
        label: event.label,
        result: allowed ? 'allowed' : 'rejected',
        tokensBefore,
        tokensAfter: tokens,
      };
      eventLog = [...eventLog, entry];

      steps.push({
        id: steps.length,
        phase: allowed ? 'allowed' : 'rejected',
        tokens,
        eventLog: [...eventLog],
        totalRequests,
        allowedCount,
        rejectedCount,
        currentEntry: entry,
        title: (allowed ? 'Allowed: ' : 'Rejected: ') + event.label,
        badge: allowed ? 'Allowed' : 'Rejected',
        explanation: allowed
          ? `Request "${event.label}" arrives. Bucket has ${tokensBefore} token${tokensBefore !== 1 ? 's' : ''}. ` +
            `1 token consumed → ${tokens} remaining. Request is ALLOWED. ` +
            (tokens === 0
              ? `The bucket is now EMPTY. The next request will be rejected until a refill tick fires.`
              : `${tokens} more request${tokens !== 1 ? 's' : ''} can be served before the bucket empties.`)
          : `Request "${event.label}" arrives. Bucket has 0 tokens — it's empty! ` +
            `No token can be consumed → request REJECTED (HTTP 429 Too Many Requests). ` +
            `The client should respect the Retry-After header and back off. ` +
            `This is temporary: tokens will refill on the next tick and normal traffic can resume.`,
      });
    } else if (event.type === 'tick') {
      const tokensBefore = tokens;
      const wasFull = tokens >= CAPACITY;
      if (!wasFull) tokens = Math.min(tokens + REFILL_RATE, CAPACITY);
      const entry = {
        id: eventLog.length,
        label: wasFull
          ? `Tick — already full, no refill (capped at ${CAPACITY})`
          : `Tick — +${REFILL_RATE} token refilled (${tokensBefore} → ${tokens})`,
        result: 'tick',
        tokensBefore,
        tokensAfter: tokens,
      };
      eventLog = [...eventLog, entry];

      steps.push({
        id: steps.length,
        phase: 'tick',
        tokens,
        eventLog: [...eventLog],
        totalRequests,
        allowedCount,
        rejectedCount,
        currentEntry: entry,
        title: wasFull ? `Tick — Bucket Full (capped at ${CAPACITY})` : `Tick — Refill +${REFILL_RATE} token`,
        badge: wasFull ? 'Cap Reached' : 'Refill',
        explanation: wasFull
          ? `A refill tick fires, but the bucket is already at full capacity (${CAPACITY}/${CAPACITY}). ` +
            `No tokens are added — the bucket is capped at ${CAPACITY}. ` +
            `This cap is intentional: clients can never accumulate more burst credit than the defined maximum, ` +
            `even if they were idle for a long time. The maximum burst size is always ${CAPACITY}.`
          : `A refill tick fires. +${REFILL_RATE} token added. ${tokensBefore} → ${tokens} tokens. ` +
            (tokensBefore === 0
              ? `The bucket was empty — this refill revives it. Requests that were being rejected ` +
                `can now proceed. Token bucket provides graceful self-healing: ` +
                `clients automatically regain access as the bucket refills over time.`
              : `Tokens accumulate between requests. As the bucket fills, ` +
                `it builds up burst capacity for future back-to-back requests.`),
      });
    }
  }

  return steps;
}

const STEPS = buildSteps();

// ─── Phase Styles ─────────────────────────────────────────────────────────────

const PHASE = {
  init:     { dot: 'bg-gray-400',    text: 'text-gray-600 dark:text-gray-400',       border: 'border-gray-200 dark:border-gray-700',     bg: 'bg-gray-50 dark:bg-gray-900/40',       badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700' },
  allowed:  { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', bg: 'bg-emerald-50 dark:bg-emerald-950/40', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  rejected: { dot: 'bg-red-500',     text: 'text-red-600 dark:text-red-400',         border: 'border-red-200 dark:border-red-800',         bg: 'bg-red-50 dark:bg-red-950/40',         badge: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300 border-red-200 dark:border-red-800' },
  tick:     { dot: 'bg-blue-500',    text: 'text-blue-600 dark:text-blue-400',       border: 'border-blue-200 dark:border-blue-800',       bg: 'bg-blue-50 dark:bg-blue-950/40',       badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300 border-blue-200 dark:border-blue-800' },
};

// ─── Token Bucket Visual ──────────────────────────────────────────────────────

function TokenBucketVisual({ step }) {
  const { tokens, phase } = step;
  const fillPct = (tokens / CAPACITY) * 100;
  const barColor = tokens === 0 ? 'bg-red-500' : tokens <= 2 ? 'bg-amber-400' : 'bg-emerald-500';
  const fillColor = tokens === 0 ? 'bg-red-100 dark:bg-red-950/30' : tokens <= 2 ? 'bg-amber-100 dark:bg-amber-950/30' : 'bg-blue-100 dark:bg-blue-950/30';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-2">
          <Droplets className="w-3.5 h-3.5 text-blue-500" />
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Token Bucket</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">capacity = {CAPACITY}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 tabular-nums font-mono">{tokens} / {CAPACITY}</span>
          <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${fillPct}%` }} />
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Bucket body */}
        <div className="flex justify-center mb-5">
          <div className="flex flex-col items-center gap-1">
            {/* Refill arrow */}
            <div className={`flex flex-col items-center gap-0.5 h-8 transition-opacity duration-300 ${phase === 'tick' && step.tokens > 0 ? 'opacity-100' : 'opacity-0'}`}>
              <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">+{REFILL_RATE} token</span>
              <span className="text-blue-400 text-base leading-none">↓</span>
            </div>

            {/* Rim */}
            <div className="w-52 h-2.5 bg-gray-300 dark:bg-gray-600 rounded-t-sm" />

            {/* Bucket */}
            <div className="w-48 h-36 border-2 border-gray-300 dark:border-gray-600 rounded-b-3xl relative overflow-hidden">
              {/* Liquid fill */}
              <div
                className={`absolute bottom-0 left-0 right-0 transition-all duration-700 ease-in-out ${fillColor}`}
                style={{ height: `${fillPct}%` }}
              />

              {/* Token grid */}
              <div className="absolute inset-0 flex flex-wrap gap-1.5 items-center justify-center p-3">
                {Array.from({ length: CAPACITY }, (_, i) => {
                  const isFilled = i < tokens;
                  return (
                    <div
                      key={i}
                      className={`w-7 h-7 rounded-full transition-all duration-500 flex items-center justify-center text-xs font-bold
                        ${isFilled
                          ? 'bg-blue-500 text-white shadow-sm shadow-blue-300 dark:shadow-blue-900'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-dashed border-gray-300 dark:border-gray-600'
                        }`}
                    >
                      {isFilled ? 'T' : '·'}
                    </div>
                  );
                })}
              </div>

              {tokens === 0 && (
                <div className="absolute inset-0 flex items-end justify-center pb-3">
                  <span className="text-xs font-bold text-red-500 bg-white/80 dark:bg-gray-900/80 px-2 py-0.5 rounded">EMPTY</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
            <p className="text-xl font-bold text-gray-700 dark:text-gray-200 tabular-nums">{step.totalRequests}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">Requests</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{step.allowedCount}</p>
            <p className="text-[10px] text-emerald-500 uppercase tracking-wide mt-0.5">Allowed</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900">
            <p className="text-xl font-bold text-red-600 dark:text-red-400 tabular-nums">{step.rejectedCount}</p>
            <p className="text-[10px] text-red-500 uppercase tracking-wide mt-0.5">Rejected</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Event Log ────────────────────────────────────────────────────────────────

function EventLog({ step }) {
  const { eventLog, currentEntry } = step;
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [currentEntry]);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
      <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 flex-shrink-0">
        <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Event Log</span>
      </div>
      <div ref={scrollRef} className="overflow-y-auto flex-1" style={{ maxHeight: '280px', scrollbarWidth: 'thin' }}>
        {eventLog.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-600 italic py-3 px-4">— no events yet —</p>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500 w-7">#</th>
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500">Event</th>
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500">Result</th>
                <th className="text-left py-1.5 px-3 font-semibold text-gray-400 dark:text-gray-500 whitespace-nowrap">Tokens</th>
              </tr>
            </thead>
            <tbody>
              {[...eventLog].reverse().map((entry) => {
                const isCurrentEntry = currentEntry && entry.id === currentEntry.id;
                return (
                  <tr
                    key={entry.id}
                    className={`border-b border-gray-50 dark:border-gray-800/50 transition-colors
                      ${isCurrentEntry ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'}`}
                  >
                    <td className="py-1.5 px-3 text-gray-300 dark:text-gray-600 tabular-nums">{entry.id + 1}</td>
                    <td className={`py-1.5 px-3 font-mono truncate max-w-[120px] ${isCurrentEntry ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                      {entry.label}
                    </td>
                    <td className="py-1.5 px-3">
                      {entry.result === 'allowed' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">✓ 200</span>
                      )}
                      {entry.result === 'rejected' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">✗ 429</span>
                      )}
                      {entry.result === 'tick' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex items-center gap-1 w-fit">
                          <Clock className="w-2.5 h-2.5" /> tick
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 px-3 font-mono text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                      {entry.tokensBefore}→{entry.tokensAfter}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Explanation Card ─────────────────────────────────────────────────────────

function ExplanationCard({ step }) {
  const c = PHASE[step.phase] || PHASE.init;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${c.dot}`} />
        <span className={`text-sm font-bold ${c.text}`}>{step.title}</span>
        {step.badge && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c.badge}`}>{step.badge}</span>
        )}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed ml-4">{step.explanation}</p>
    </div>
  );
}

// ─── Step Sidebar ─────────────────────────────────────────────────────────────

function StepSidebar({ steps, currentIdx, onJump }) {
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentIdx]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 py-2 flex-shrink-0">Events</p>
      <div className="flex-1 overflow-y-auto space-y-0.5 px-2 pb-2" style={{ scrollbarWidth: 'thin' }}>
        {steps.map((s, i) => {
          const c = PHASE[s.phase] || PHASE.init;
          const isCurrent = i === currentIdx;
          const isPast = i < currentIdx;
          return (
            <button
              key={i}
              ref={isCurrent ? activeRef : null}
              onClick={() => onJump(i)}
              className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-[11px] transition-all duration-150
                ${isCurrent ? `${c.bg} border ${c.border} font-semibold`
                  : isPast ? 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-300 dark:text-gray-600'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCurrent ? c.dot : isPast ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              <span className={`font-mono leading-tight truncate ${isCurrent ? c.text : ''}`}>
                <span className="text-gray-300 dark:text-gray-600 mr-1">{String(i + 1).padStart(2, '0')}.</span>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TokenBucket() {
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = STEPS.length;
  const step = STEPS[idx];

  const goTo = (i) => {
    setIdx(Math.max(0, Math.min(total - 1, i)));
    setAnimKey(k => k + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { setIdx(i => Math.min(i + 1, total - 1)); setAnimKey(k => k + 1); }
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { setIdx(i => Math.max(i - 1, 0));          setAnimKey(k => k + 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total]);

  const c = PHASE[step.phase] || PHASE.init;

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden">

      {/* Top bar */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/dev" className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dev</span>
            </Link>
            <span className="text-gray-300 dark:text-gray-700">/</span>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Token Bucket — Rate Limiting Simulator</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 tabular-nums">{idx + 1} / {total}</span>
            <div className="hidden sm:flex w-28 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
            <span className="hidden sm:inline text-xs text-gray-400">← → keys</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        <div className="hidden lg:flex flex-col w-52 xl:w-60 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 overflow-hidden">
          <StepSidebar steps={STEPS} currentIdx={idx} onJump={goTo} />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div key={animKey} className="max-w-4xl mx-auto px-4 py-5 space-y-5" style={{ animation: 'fadeSlideIn 0.25s ease' }}>
            <ExplanationCard step={step} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <TokenBucketVisual step={step} />
              <EventLog step={step} />
            </div>
            <div className="h-4" />
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm z-10">
        <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <button onClick={() => goTo(idx - 1)} disabled={idx === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <div className="hidden lg:flex items-center gap-2 text-xs text-gray-400">
            {step.badge && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${c.badge}`}>{step.badge}</span>}
            <span className="font-mono">{step.title}</span>
          </div>
          <div className="flex lg:hidden items-center gap-2 text-xs text-gray-400">
            <div className="w-32 h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
            </div>
            <span className="tabular-nums">{idx + 1}/{total}</span>
          </div>
          <button onClick={() => goTo(idx + 1)} disabled={idx === total - 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-transparent bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
